import React from 'react';
import { useTranslation } from 'react-i18next';
import usePushNotifications from '../../hooks/usePushNotifications';
import './PushSettings.css';

/**
 * Kategorierna som kan togglas per spelare, med sina i18n-nycklar.
 * Fältnamnen speglar NotificationPreference i Django.
 * @type {Array<{key: string, labelKey: string}>}
 */
const CATEGORIES = [
  { key: 'push_queue', labelKey: 'settings.pushQueue' },
  { key: 'push_match', labelKey: 'settings.pushMatch' },
  { key: 'push_economy', labelKey: 'settings.pushEconomy' },
  { key: 'push_achievements', labelKey: 'settings.pushAchievements' },
];

/**
 * Inställningskort för push-notiser till enheten.
 *
 * Kortet visas bara när webbläsaren stöder push och servern har VAPID-nycklar
 * — en knapp som aldrig kan fungera är värre än ingen knapp. På iOS krävs att
 * sajten lagts till på hemskärmen först, vilket förklaras i stället för att
 * visa en avaktiverad knapp utan orsak.
 *
 * Till skillnad från de befintliga notisvalen (som ligger i localStorage och
 * bara filtrerar toasts i den öppna fliken) sparas dessa på servern — push
 * avgörs där, så valen måste finnas där.
 */
export default function PushSettings() {
  const { t } = useTranslation();
  const {
    supported,
    configured,
    permission,
    subscribed,
    prefs,
    devices,
    loading,
    error,
    needsInstall,
    subscribe,
    unsubscribe,
    savePrefs,
  } = usePushNotifications();

  if (loading) return null;
  // Inget att erbjuda: webbläsaren kan inte, eller servern saknar nycklar.
  if (!supported || !configured) return null;

  const blocked = permission === 'denied';

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">{t('settings.pushTitle')}</h2>
      <p className="settings-card-desc">{t('settings.pushDesc')}</p>

      {needsInstall && (
        <p className="push-settings-note">{t('settings.pushIosHint')}</p>
      )}

      {blocked && (
        <p className="push-settings-note push-settings-note-warning">
          {t('settings.pushBlocked')}
        </p>
      )}

      {error && <p className="push-settings-error">{error}</p>}

      <div className="settings-row settings-toggle-row">
        <label className="settings-label">
          {t('settings.pushEnable')}
          {devices > 0 && (
            <span className="push-settings-devices">
              {t('settings.pushDevices', { count: devices })}
            </span>
          )}
        </label>
        <button
          type="button"
          className={`push-settings-button${subscribed ? ' active' : ''}`}
          onClick={subscribed ? unsubscribe : subscribe}
          disabled={blocked || needsInstall}
        >
          {subscribed ? t('settings.pushOff') : t('settings.pushOn')}
        </button>
      </div>

      {subscribed && prefs && (
        <>
          <div className="settings-row settings-toggle-row">
            <label className="settings-label">{t('settings.pushMaster')}</label>
            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={prefs.push_enabled !== false}
                onChange={(e) => savePrefs({ push_enabled: e.target.checked })}
              />
              <span className="settings-toggle-slider" />
            </label>
          </div>

          {CATEGORIES.map((cat) => (
            <div key={cat.key} className="settings-row settings-toggle-row">
              <label className="settings-label push-settings-sub">
                {t(cat.labelKey)}
              </label>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={prefs[cat.key] !== false}
                  disabled={prefs.push_enabled === false}
                  onChange={(e) => savePrefs({ [cat.key]: e.target.checked })}
                />
                <span className="settings-toggle-slider" />
              </label>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
